/**
 * Performance monitoring utilities for tracking and reporting performance metrics
 */

import React from 'react';

interface PerformanceMetric {
    name: string;
    value: number;
    timestamp: number;
}

class PerformanceMonitor {
    private metrics: PerformanceMetric[] = [];
    private enabled: boolean;

    constructor() {
        // Enable performance monitoring in production
        this.enabled = import.meta.env.PROD;
    }

    /**
     * Mark a performance measurement point
     */
    mark(name: string): void {
        if (!this.enabled) return;

        try {
            performance.mark(name);
        } catch (error) {
            console.warn('Performance mark failed:', error);
        }
    }

    /**
     * Measure time between two marks
     */
    measure(name: string, startMark: string, endMark: string): number | null {
        if (!this.enabled) return null;

        try {
            performance.measure(name, startMark, endMark);
            const measure = performance.getEntriesByName(name, 'measure')[0];

            if (measure) {
                const metric: PerformanceMetric = {
                    name,
                    value: measure.duration,
                    timestamp: Date.now(),
                };

                this.metrics.push(metric);

                // Log slow operations (> 1000ms)
                if (measure.duration > 1000) {
                    console.warn(`Slow operation detected: ${name} took ${measure.duration.toFixed(2)}ms`);
                }

                return measure.duration;
            }
        } catch (error) {
            console.warn('Performance measure failed:', error);
        }

        return null;
    }

    /**
     * Record a custom metric
     */
    recordMetric(name: string, value: number): void {
        if (!this.enabled) return;

        const metric: PerformanceMetric = {
            name,
            value,
            timestamp: Date.now(),
        };

        this.metrics.push(metric);
    }

    /**
     * Get Core Web Vitals
     */
    getCoreWebVitals(): void {
        if (!this.enabled) return;

        // Largest Contentful Paint (LCP)
        try {
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const lastEntry = entries[entries.length - 1];

                this.recordMetric('LCP', lastEntry.startTime);

                // Good: < 2.5s, Needs improvement: 2.5-4s, Poor: > 4s
                if (lastEntry.startTime > 4000) {
                    console.warn(`Poor LCP: ${lastEntry.startTime.toFixed(2)}ms`);
                }
            });

            observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (error) {
            // LCP not supported
        }

        // First Input Delay (FID) - via PerformanceObserver
        try {
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                entries.forEach((entry: any) => {
                    this.recordMetric('FID', entry.processingStart - entry.startTime);

                    // Good: < 100ms, Needs improvement: 100-300ms, Poor: > 300ms
                    const fid = entry.processingStart - entry.startTime;
                    if (fid > 300) {
                        console.warn(`Poor FID: ${fid.toFixed(2)}ms`);
                    }
                });
            });

            observer.observe({ entryTypes: ['first-input'] });
        } catch (error) {
            // FID not supported
        }

        // Cumulative Layout Shift (CLS)
        try {
            let clsValue = 0;
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries() as any[]) {
                    if (!entry.hadRecentInput) {
                        clsValue += entry.value;
                    }
                }

                this.recordMetric('CLS', clsValue);

                // Good: < 0.1, Needs improvement: 0.1-0.25, Poor: > 0.25
                if (clsValue > 0.25) {
                    console.warn(`Poor CLS: ${clsValue.toFixed(3)}`);
                }
            });

            observer.observe({ entryTypes: ['layout-shift'] });
        } catch (error) {
            // CLS not supported
        }
    }

    /**
     * Get navigation timing metrics
     */
    getNavigationTiming(): Record<string, number> | null {
        if (!this.enabled) return null;

        try {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

            if (navigation) {
                return {
                    dns: navigation.domainLookupEnd - navigation.domainLookupStart,
                    tcp: navigation.connectEnd - navigation.connectStart,
                    request: navigation.responseStart - navigation.requestStart,
                    response: navigation.responseEnd - navigation.responseStart,
                    dom: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
                    load: navigation.loadEventEnd - navigation.loadEventStart,
                    total: navigation.loadEventEnd - navigation.fetchStart,
                };
            }
        } catch (error) {
            console.warn('Navigation timing not available:', error);
        }

        return null;
    }

    /**
     * Get all recorded metrics
     */
    getMetrics(): PerformanceMetric[] {
        return [...this.metrics];
    }

    /**
     * Clear all metrics
     */
    clearMetrics(): void {
        this.metrics = [];

        try {
            performance.clearMarks();
            performance.clearMeasures();
        } catch (error) {
            // Ignore
        }
    }

    /**
     * Report metrics to analytics service (placeholder)
     */
    reportMetrics(): void {
        if (!this.enabled || this.metrics.length === 0) return;

        // In a real application, you would send these to an analytics service
        // like Google Analytics, DataDog, New Relic, etc.
        console.log('Performance Metrics:', this.metrics);

        const navigationTiming = this.getNavigationTiming();
        if (navigationTiming) {
            console.log('Navigation Timing:', navigationTiming);
        }
    }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Initialize Core Web Vitals monitoring
if (typeof window !== 'undefined') {
    performanceMonitor.getCoreWebVitals();

    // Report metrics when page is about to unload
    window.addEventListener('beforeunload', () => {
        performanceMonitor.reportMetrics();
    });
}

/**
 * React hook for measuring component render performance
 */
export function usePerformanceMonitor(componentName: string) {
    const startMark = `${componentName}-start`;
    const endMark = `${componentName}-end`;
    const measureName = `${componentName}-render`;

    return {
        start: () => performanceMonitor.mark(startMark),
        end: () => {
            performanceMonitor.mark(endMark);
            return performanceMonitor.measure(measureName, startMark, endMark);
        },
    };
}

// Export for use in other modules
export default performanceMonitor;
